import { useCallback, useState } from "react";

// Filter state that lives in the page URL (?status=ready), the convention the admin app already uses (?tracking=, ?edit=, ?conversation=, ?tab=).
// A filter chosen on a dashboard card, or changed on the page, survives a refresh and is restored by Back/Forward because the page reads it
// from the URL when it mounts.

export function buildQueryString(query = {}) {
  const params = new URLSearchParams();
  Object.entries(query || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    params.set(key, String(value));
  });
  return params.toString();
}

export function withQuery(path, query) {
  const queryString = buildQueryString(query);
  return queryString ? `${path}?${queryString}` : path;
}

export const isDateParam = (value) => /^\d{4}-\d{2}-\d{2}$/.test(String(value || "")) && !Number.isNaN(Date.parse(value));

// Reads one query parameter. `allowed` is a list of accepted values or a predicate; anything else (a stale or hand-edited URL) falls back.
export function readQueryParam(name, allowed, fallback = "", search = typeof window === "undefined" ? "" : window.location.search) {
  const value = new URLSearchParams(search).get(name);
  if (value === null) return fallback;
  if (typeof allowed === "function") return allowed(value) ? value : fallback;
  if (Array.isArray(allowed)) return allowed.includes(value) ? value : fallback;
  return value;
}

// Writes one query parameter without adding a history entry and without touching the others. The default value removes the parameter.
export function replaceQueryParam(name, value, defaultValue = "") {
  const url = new URL(window.location.href);
  if (value === undefined || value === null || value === "" || value === defaultValue) url.searchParams.delete(name);
  else url.searchParams.set(name, String(value));
  const next = `${url.pathname}${url.search}${url.hash}`;
  if (next !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
    window.history.replaceState(window.history.state, "", next);
  }
}

// useState whose initial value comes from the URL and whose changes are written back to it.
export function useUrlFilter(name, allowed, defaultValue = "all") {
  const [value, setValue] = useState(() => readQueryParam(name, allowed, defaultValue));
  const update = useCallback((next) => {
    setValue(next);
    replaceQueryParam(name, next, defaultValue);
  }, [defaultValue, name]);
  return [value, update];
}
